export type ErrorType =
  | "auth"
  | "validation"
  | "network"
  | "permission"
  | "not-found"
  | "server"
  | "unknown";

export interface ClassifiedError {
  type: ErrorType;
  message: string;
  originalError: unknown;
  canRetry: boolean;
  requiresAuth: boolean;
}

export function classifyError(error: unknown): ClassifiedError {
  const message = getErrorMessage(error);
  const messageLower = message.toLowerCase();

  if (
    messageLower.includes("not authenticated") ||
    messageLower.includes("unauthenticated") ||
    messageLower.includes("no autorizado") ||
    messageLower.includes("unauthorized") ||
    messageLower.includes("token") ||
    messageLower.includes("session")
  ) {
    return {
      type: "auth",
      message: "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.",
      originalError: error,
      canRetry: false,
      requiresAuth: true,
    };
  }

  if (
    messageLower.includes("permission") ||
    messageLower.includes("permiso") ||
    messageLower.includes("forbidden") ||
    messageLower.includes("acceso denegado")
  ) {
    return {
      type: "permission",
      message: "No tienes permisos para realizar esta acción.",
      originalError: error,
      canRetry: false,
      requiresAuth: false,
    };
  }

  if (
    messageLower.includes("validation") ||
    messageLower.includes("invalid") ||
    messageLower.includes("inválido") ||
    messageLower.includes("requerido") ||
    messageLower.includes("required") ||
    messageLower.includes("debe ser") ||
    messageLower.includes("must be")
  ) {
    return {
      type: "validation",
      message,
      originalError: error,
      canRetry: false,
      requiresAuth: false,
    };
  }

  if (
    messageLower.includes("not found") ||
    messageLower.includes("no encontrado") ||
    messageLower.includes("does not exist") ||
    messageLower.includes("no existe")
  ) {
    return {
      type: "not-found",
      message: "El recurso solicitado no fue encontrado.",
      originalError: error,
      canRetry: false,
      requiresAuth: false,
    };
  }

  if (
    messageLower.includes("network") ||
    messageLower.includes("red") ||
    messageLower.includes("connection") ||
    messageLower.includes("conexión") ||
    messageLower.includes("fetch") ||
    messageLower.includes("timeout")
  ) {
    return {
      type: "network",
      message: "Error de conexión. Verifica tu conexión a internet.",
      originalError: error,
      canRetry: true,
      requiresAuth: false,
    };
  }

  if (
    messageLower.includes("server error") ||
    messageLower.includes("internal error") ||
    messageLower.includes("500") ||
    messageLower.includes("error del servidor")
  ) {
    return {
      type: "server",
      message: "Error del servidor. Estamos trabajando en solucionarlo.",
      originalError: error,
      canRetry: true,
      requiresAuth: false,
    };
  }

  return {
    type: "unknown",
    message: message || "Ha ocurrido un error inesperado.",
    originalError: error,
    canRetry: true,
    requiresAuth: false,
  };
}

function getErrorMessage(error: unknown): string {
  let message = "";

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "string") {
    message = error;
  } else if (error && typeof error === "object" && "message" in error) {
    message = String((error as any).message);
  } else {
    message = "Unknown error";
  }

  const errorPattern = /Uncaught Error:\s*(.+?)\s+at\s/i;
  const match = message.match(errorPattern);

  if (match && match[1]) {
    message = match[1].trim();
  } else {
    message = message.replace(/\[CONVEX[^\]]*\]\s*/g, "");
    message = message.replace(/\[Request\s*ID[^\]]*\]\s*/g, "");
    message = message.replace(/\[Error\s*ID[^\]]*\]\s*/g, "");
    message = message.replace(/\[Function[^\]]*\]\s*/g, "");
    message = message.replace(/Server\s*Error\s*/gi, "");
    message = message.replace(/Uncaught\s*Error:\s*/gi, "");
    message = message.replace(/Error:\s*/gi, "");
    message = message.replace(/\s*Called\s*by\s*client\s*$/gi, "");
    message = message.replace(/\s*at\s+client\s*$/gi, "");

    const stackStart = message.search(/\n\s+at\s|\n\s*at\s|\sat\s/);
    if (stackStart !== -1) {
      message = message.substring(0, stackStart);
    }

    message = message.replace(/\s+/g, " ").trim();
  }

  if (!message) {
    message = "Ha ocurrido un error. Por favor, inténtalo de nuevo.";
  }

  return message;
}

export function logError(
  error: ClassifiedError,
  context?: {
    route?: string;
    userId?: string;
    action?: string;
  }
): void {
  console.error("[Error Handler]", {
    type: error.type,
    message: error.message,
    canRetry: error.canRetry,
    requiresAuth: error.requiresAuth,
    context,
    originalError: error.originalError,
    timestamp: new Date().toISOString(),
  });
}
